import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Typography,
    Slide,
    Paper,
    Autocomplete,
    TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react/dist/iconify.js';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const AddGateKeeperModal = ({ open, handleClose, editData = null }) => {
    const [formData, setFormData] = useState({
        login: '',
        password: '',
        societyId: null,
        phone: '',
        remark: '',
        status: true,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Sample societies for dropdown
    const societies = [
        { id: 1, name: 'Santosh Society' },
        { id: 2, name: 'Demo Jll' },
        { id: 3, name: 'JLL Test' },
        { id: 4, name: 'Onboarding Society' },
        { id: 5, name: 'Zipgrid Family' },
        { id: 6, name: 'HSS Family' },
        { id: 7, name: 'Haunted Societyyew' },
        { id: 8, name: 'IFCI' },
        { id: 9, name: 'Demo JLL Site' },
    ];

    useEffect(() => {
        if (editData) {
            const society = societies.find(s => s.name === editData.society);
            setFormData({
                login: editData.login || '',
                password: '',
                societyId: society || null,
                phone: editData.phone || '',
                remark: editData.remark || '',
                status: editData.status ?? true,
            });
        } else {
            setFormData({
                login: '',
                password: '',
                societyId: null,
                phone: '',
                remark: '',
                status: true,
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editData, open]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            console.log('Submitting:', formData);
            await new Promise(resolve => setTimeout(resolve, 1000));
            handleClose();
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                }
            }}
        >
            {/* Header with Gradient Background */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    color: 'white',
                    position: 'relative',
                }}
            >
                {/* Background Pattern */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.1,
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}
                />

                <DialogTitle sx={{ p: 2.5, pb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Icon icon="mdi:account-key" width={24} height={24} />
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', fontSize: '1.1rem' }}>
                                    {editData ? 'Edit GateKeeper' : 'Add New GateKeeper'}
                                </Typography>
                                <p className='tw-text-white tw-text-sm'>
                                    {editData ? 'Update gatekeeper credentials' : 'Add a new gatekeeper login'}
                                </p>
                            </Box>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={handleClose}
                            size="small"
                            sx={{
                                color: 'white',
                                backgroundColor: 'rgba(255,255,255,0.15)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.25)',
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
            </Box>

            <form onSubmit={handleSubmit}>
                <DialogContent sx={{ p: 0, bgcolor: '#f8fafc' }}>
                    <Box sx={{ p: 2.5 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.5,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: 'background.paper',
                            }}
                        >
                            {/* Society Selection */}
                            <Box sx={{ mb: 2.5 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
                                >
                                    <Icon icon="mdi:office-building" width={16} />
                                    Society <span style={{ color: '#ef4444' }}>*</span>
                                </Typography>
                                <Autocomplete
                                    size="small"
                                    options={societies}
                                    getOptionLabel={(option) => option.name}
                                    value={formData.societyId}
                                    onChange={(e, newValue) => setFormData(prev => ({ ...prev, societyId: newValue }))}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Select Society"
                                            required
                                        />
                                    )}
                                />
                            </Box>

                            {/* Login & Password Row */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2.5 }}>
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
                                    >
                                        <Icon icon="mdi:account" width={16} />
                                        Login <span style={{ color: '#ef4444' }}>*</span>
                                    </Typography>
                                    <input
                                        type="text"
                                        name="login"
                                        value={formData.login}
                                        onChange={handleChange}
                                        className="form-control radius-8"
                                        placeholder="Enter login username"
                                        required
                                        style={{ fontSize: '0.875rem' }}
                                    />
                                </Box>
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
                                    >
                                        <Icon icon="mdi:lock" width={16} />
                                        Password {!editData && <span style={{ color: '#ef4444' }}>*</span>}
                                    </Typography>
                                    <input
                                        type="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        className="form-control radius-8"
                                        placeholder={editData ? "Leave blank to keep current" : "Enter password"}
                                        required={!editData}
                                        style={{ fontSize: '0.875rem' }}
                                    />
                                </Box>
                            </Box>

                            {/* Phone */}
                            <Box sx={{ mb: 2.5 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
                                >
                                    <Icon icon="mdi:phone" width={16} />
                                    Phone Number
                                </Typography>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className="form-control radius-8"
                                    placeholder="Enter phone number"
                                    style={{ fontSize: '0.875rem' }}
                                />
                            </Box>

                            {/* Remark */}
                            <Box sx={{ mb: 2.5 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
                                >
                                    <Icon icon="mdi:note-text" width={16} />
                                    Remark
                                </Typography>
                                <textarea
                                    name="remark"
                                    value={formData.remark}
                                    onChange={handleChange}
                                    className="form-control radius-8"
                                    placeholder="Enter any remarks"
                                    rows={3}
                                    style={{ fontSize: '0.875rem', resize: 'none' }}
                                />
                            </Box>

                            {/* Status */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 2,
                                    borderRadius: 2,
                                    backgroundColor: formData.status ? '#f0fdf4' : '#fef2f2',
                                    border: '1px solid',
                                    borderColor: formData.status ? '#bbf7d0' : '#fecaca',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Icon
                                        icon={formData.status ? "mdi:check-circle" : "mdi:close-circle"}
                                        width={24}
                                        style={{ color: formData.status ? '#22c55e' : '#ef4444' }}
                                    />
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                            Status
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {formData.status ? 'GateKeeper is active and can login' : 'GateKeeper is inactive and cannot login'}
                                        </Typography>
                                    </Box>
                                </Box>
                                <div className="form-switch">
                                    <input
                                        type="checkbox"
                                        name="status"
                                        checked={formData.status}
                                        onChange={handleChange}
                                        className="form-check-input"
                                        id="gatekeeperStatus"
                                        style={{ width: '44px', height: '24px', cursor: 'pointer' }}
                                    />
                                </div>
                            </Box>
                        </Paper>
                    </Box>
                </DialogContent>

                {/* Actions */}
                <DialogActions sx={{ px: 2.5, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1.5, bgcolor: 'white' }}>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="btn btn-outline-secondary radius-8 px-20 py-10 text-sm fw-medium"
                        disabled={isSubmitting}
                        style={{ flex: 1 }}
                    >
                        <Icon icon="solar:close-circle-outline" className="me-6" />
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary-600 radius-8 px-20 py-10 text-sm fw-medium"
                        disabled={isSubmitting}
                        style={{ flex: 1 }}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-8" role="status" aria-hidden="true"></span>
                                Saving...
                            </>
                        ) : (
                            <>
                                <Icon icon="solar:check-circle-bold" className="me-6" />
                                {editData ? 'Update GateKeeper' : 'Save GateKeeper'}
                            </>
                        )}
                    </button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AddGateKeeperModal;
